import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MEDIA_QUERIES, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import UiComponentView from 'inquiry/components/UiComponentView';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { FooterComponent, ParentComponentProps } from 'lib/interfaces/ui-components';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';
import { Z_INDICES } from 'verify/styles/variables';

// export this styled component so that we can override the position:fixed style for inquiry template editor
export const Container = styled.div<{
  direction: FooterComponent['attributes']['direction'];
  styles: FooterComponent['styles'];
}>`
  position: absolute;
  z-index: ${Z_INDICES.footer};
  left: 0;
  right: 0;
  bottom: 0;
  border-style: solid;

  display: flex;
  flex-direction: ${({ direction }) => {
    switch (direction) {
      case 'end_to_start':
        return 'row-reverse';
      case 'top_to_bottom':
        return 'column';
      case 'start_to_end':
      default:
        return 'row';
    }
  }};
  align-items: ${({ styles }) => styles?.justify?.base?.base ?? 'center'};
  justify-content: space-between;

  @media ${MEDIA_QUERIES.isMobile} {
    position: fixed;
  }

  ${({ styles }) => css`
    background-color: ${colorCss(styles?.['background-color'].base.base)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    padding-top: ${sizeCss(styles?.padding.base.base?.top as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    padding-bottom: ${sizeCss(styles?.padding.base.base?.bottom as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    border-top-width: ${sizeCss(styles?.['border-width'].base.base?.top as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    border-bottom-width: ${sizeCss(styles?.['border-width'].base.base?.bottom as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    border-left-width: ${sizeCss(styles?.['border-width'].base.base?.left as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    border-right-width: ${sizeCss(styles?.['border-width'].base.base?.right as any)};
    border-top-left-radius: ${sizeCss(styles?.['border-radius'].base.base)};
    border-top-right-radius: ${sizeCss(styles?.['border-radius'].base.base)};
    border-color: ${colorCss(styles?.['border-color'].base.base)};
    /* IE11 fallbacks */
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    padding-left: ${sizeCss(styles?.padding.base.base?.left as any)};
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    padding-right: ${sizeCss(styles?.padding.base.base?.right as any)};
    /* For Apple devices with notches; will be ignored for other devices */
    /* This allows us to use object-fit=cover in the HTML's meta tag */
    /* https://webkit.org/blog/7929/designing-websites-for-iphone-x/ */
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    padding-left: calc( ${sizeCss(
      styles?.padding.base.base?.left as any,
    )} + env(safe-area-inset-left))};
    padding-right: calc( ${sizeCss(
      /* TODO [strict-migration] The following line is not typed properly; please fix! */
      styles?.padding.base.base?.right as any,
    )} + env(safe-area-inset-right))}; `}
`;

interface FooterProps extends ParentComponentProps {
  component: FooterComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

const Footer = ({ component, ...props }: FooterProps) => {
  const [spacerHeight, setSpacerHeight] = useState(SPACINGS.base * 7);
  const containerRef = useRef<HTMLDivElement>();

  // We want to guarantee sufficient top margin for the footer so it doesn't
  // cover elements near the bottom of the flow
  const measureFooterAndSetSpacerHeight = () => {
    const footerHeight =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      containerRef.current?.getBoundingClientRect().height - parseInt(SPACINGS.widgetPadding);
    if (footerHeight >= 0) {
      setSpacerHeight(Math.floor(footerHeight));
    }
  };

  useLayoutEffect(() => {
    measureFooterAndSetSpacerHeight();
  });

  // Fire height calculation on first tick; sometimes height will be 0 on initial render
  useEffect(() => {
    const timeout = setTimeout(measureFooterAndSetSpacerHeight, 0);
    return () => clearTimeout(timeout);
  }, []);

  // div allows fixed position while still taking space.
  // Note that we cannot use position: sticky because of IE11.
  return (
    <>
      <div aria-hidden style={{ height: spacerHeight }} />
      <Container
        data-test='footer'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={containerRef}
        direction={component.attributes.direction}
        styles={component.styles}
      >
        <>
          {!props.isPreview &&
            component.attributes.children.map((childComponent) => (
              <UiComponentView {...props} key={childComponent.name} component={childComponent} />
            ))}
          {props.isPreview &&
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            props.childPreviews.map((childPreview) => (
              <Fragment key={childPreview.name}>{childPreview.component}</Fragment>
            ))}
        </>
      </Container>
    </>
  );
};

export default Footer;
